/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { BANNER_AD_PLACEMENTS, BannerAdRowContainer } from '@coursera-web/promo-units';
import {
  ArticleFooter,
  ArticleHeader,
  DEFAULT_ARTICLE_AUTHOR,
  getListOfAuthorNames,
} from '@coursera-web/shared-articles';

import config from 'js/app/config';
import path from 'js/lib/path';

// CDS
import { breakpoints } from '@coursera/cds-core';

import { useGenerateStartFreeTrialTestEpicImpression } from 'bundles/coursera-plus-common/components/elevateLTV/startFreeTrial/useGenerateStartFreeTrialTestEpicImpression';
import { getShouldShowCourseraPlusMerchandisingPlacements } from 'bundles/coursera-plus-common/utils/subscriptionsForwardDiscoveryExperiment';
import { useCourseraPlusProductOwnerships } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import ArticleSources from 'bundles/unified-seo-content-common/components/ArticleSources';
import CPlusElevateLTVPromoUnit from 'bundles/unified-seo-content-common/components/promo-unit/CPlusElevateLTVPromoUnit';
import RichTextContentRenderer from 'bundles/unified-seo-content-common/components/rich-text/RichTextContentRenderer';
import ContentPageSEO from 'bundles/unified-seo-content-common/components/seo/ContentPageSEO';
import { SEO_CONTENT_PAGE_TYPE } from 'bundles/unified-seo-content-common/constants';
import useEstimatedReadTimeData from 'bundles/unified-seo-content-common/hooks/useEstimatedReadTimeData';
import type { ArticleContentPageData } from 'bundles/unified-seo-content-common/providers/article/ArticleContentPageDataProvider';
import { getCategoryBreadcrumbLink } from 'bundles/unified-seo-content-common/utils/hubPageUtils';

import _t from 'i18n!nls/articles';

const styles = {
  articlePage: css`
    background-color: var(--cds-color-neutral-background-primary);
    overflow-x: hidden;
    overflow-y: auto;
    position: relative;
    width: 100%;
  `,
  articleContainer: css`
    max-width: 782px;
    margin: 0 auto;
    padding: 0 16px;

    ${breakpoints.down('xs')} {
      width: 100%;
    }
  `,
  contentContainer: css`
    margin-bottom: 4rem;
  `,
};

const ArticleContentPage = ({
  locales,
  pageData,
  slug,
  subfolderLocaleCode,
  defaultRegionCountryCode,
  hubPageLink,
  isPreview,
}: ArticleContentPageData) => {
  const { estimatedReadTime } = useEstimatedReadTimeData({ slug });
  const urlDomain = config.url.domain;

  useGenerateStartFreeTrialTestEpicImpression({});
  const { ownsCourseraPlus } = useCourseraPlusProductOwnerships();
  const shouldShowElevateLTVPromoUnit = getShouldShowCourseraPlusMerchandisingPlacements() && !ownsCourseraPlus;

  if (!pageData) {
    return null;
  }

  const { title, intro, featuredImage, featuredImageLink, hideFeaturedImage, body, sources, sys, authorsCollection } =
    pageData;

  if (!title || !slug || !intro || !body || !body?.json) {
    return null;
  }

  const pageType = SEO_CONTENT_PAGE_TYPE.articles;

  const authors =
    authorsCollection?.items
      ?.map((author) => ({
        name: author?.name,
        url: author?.slug ? path.join(urlDomain, '/articles/author/', author.slug) : undefined,
        jobTitle: author?.jobTitle,
        externalWebsites: author?.externalWebsites,
        description: author?.description,
        image: author?.image?.url ? `${config.url.imageProxyRoot}${author.image.url}` : undefined,
      }))
      .filter((author) => Boolean(author && author.name && author.url)) || undefined;

  const countryCode = subfolderLocaleCode?.countryCode;

  const categoryBreadcrumbLinks = getCategoryBreadcrumbLink({ pageData, countryCode, pageType });

  const firstPublishedAt = sys?.firstPublishedAt;
  const featuredImageCaption = featuredImage?.title;
  const publishedAt = sys?.publishedAt;

  const currDate = new Date();

  const byLineData = {
    authorNames: getListOfAuthorNames(authors) || DEFAULT_ARTICLE_AUTHOR.name,
    // If this is undefined, someone is seeing this article in preview.
    // In that case, set current date as default.
    date: publishedAt || currDate.toISOString(),
    title,
  };

  const bodyJson = body.json;
  const links = body?.links;
  const { authorNames, date } = byLineData;

  const breadcrumbLinks = [{ href: hubPageLink, label: _t('Articles') }, ...categoryBreadcrumbLinks];

  const showArticleFooter = isPreview ? !!title : !!firstPublishedAt && !!title;

  const renderBannerAds = () => {
    return shouldShowElevateLTVPromoUnit ? (
      <CPlusElevateLTVPromoUnit size="large" />
    ) : (
      <BannerAdRowContainer placement={BANNER_AD_PLACEMENTS.ARTICLE} />
    );
  };

  return (
    <div className="rc-ArticlePage cds" data-e2e="article-page">
      <div css={styles.articlePage}>
        <div css={styles.articleContainer} style={{ minHeight: '100vh' }}>
          <ArticleHeader
            title={title}
            featuredImage={hideFeaturedImage ? null : featuredImage}
            featuredImageCaption={hideFeaturedImage ? null : featuredImageCaption}
            featuredImageLink={hideFeaturedImage ? null : featuredImageLink}
            subtitle={intro}
            authorNames={authorNames}
            latestPublishedDate={date}
            breadcrumbLinks={breadcrumbLinks}
          />
          <div css={styles.contentContainer}>
            <RichTextContentRenderer body={bodyJson} links={links} />
          </div>
          <ArticleSources body={sources?.json} />
          {showArticleFooter && (
            <ArticleFooter
              authorNames={authorNames}
              authorsProfiles={authors}
              date={date}
              title={title}
              shouldDisplayAuthorsProfile
            />
          )}
        </div>
        {renderBannerAds()}
        <ContentPageSEO
          slug={slug}
          pageData={pageData}
          locales={locales}
          defaultRegionCountryCode={defaultRegionCountryCode}
          hubPageLink={hubPageLink}
          subfolderLocaleCode={subfolderLocaleCode}
          authors={authors}
          estimatedReadTime={estimatedReadTime}
          pageType={pageType}
        />
      </div>
    </div>
  );
};

export default ArticleContentPage;
